// pages/admin/AnalyticsPage.tsx
/**
 * @description Responsive analytics dashboard with charts and metrics
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  TrendingUp,
  QrCode,
  Mail,
  Eye,
  Calendar,
  Download
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { AdminLayout } from '../../components/layout';
import { Button, Card, TabsRoot, TabsList, TabsTrigger, TabsContent } from '../../components/ui';
import { useAnalyticsMetrics } from '../../hooks/useAnalytics';
import { MOCK_BUILDING } from '../../lib/mockData';

// Demo data for charts
const dailyData = [
  { date: 'Dec 1', scans: 12, emails: 3, views: 28 },
  { date: 'Dec 2', scans: 8, emails: 2, views: 22 },
  { date: 'Dec 3', scans: 15, emails: 5, views: 35 },
  { date: 'Dec 4', scans: 10, emails: 4, views: 30 },
  { date: 'Dec 5', scans: 18, emails: 6, views: 42 },
  { date: 'Dec 6', scans: 22, emails: 8, views: 55 },
  { date: 'Dec 7', scans: 14, emails: 3, views: 38 },
  { date: 'Dec 8', scans: 16, emails: 5, views: 40 },
  { date: 'Dec 9', scans: 20, emails: 7, views: 48 },
  { date: 'Dec 10', scans: 25, emails: 9, views: 62 },
  { date: 'Dec 11', scans: 19, emails: 6, views: 45 },
  { date: 'Dec 12', scans: 23, emails: 8, views: 58 },
  { date: 'Dec 13', scans: 28, emails: 10, views: 70 },
  { date: 'Dec 14', scans: 21, emails: 7, views: 52 },
];

const unitTypeData = [
  { name: 'Bachelor', views: 45, color: '#3b82f6' },
  { name: '1-BR', views: 120, color: '#10b981' },
  { name: '1BR+Den', views: 85, color: '#f59e0b' },
  { name: '2-BR', views: 95, color: '#8b5cf6' },
];

const hourlyData = [
  { hour: '9am', scans: 5 },
  { hour: '10am', scans: 12 },
  { hour: '11am', scans: 18 },
  { hour: '12pm', scans: 25 },
  { hour: '1pm', scans: 22 },
  { hour: '2pm', scans: 28 },
  { hour: '3pm', scans: 32 },
  { hour: '4pm', scans: 24 },
  { hour: '5pm', scans: 35 },
  { hour: '6pm', scans: 30 },
  { hour: '7pm', scans: 18 },
  { hour: '8pm', scans: 10 },
];

export function AnalyticsPage() {
  const { slug } = useParams<{ slug: string }>();
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  // TODO: Replace with actual building lookup
  const building = MOCK_BUILDING;

  const { metrics } = useAnalyticsMetrics({
    buildingId: building.id,
    dateRange
  });

  const handleExport = () => {
    // TODO: Implement CSV export
    console.log('Exporting analytics data...');
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Breadcrumb */}
        <div className="mb-4 lg:mb-6">
          <Link
            to={`/admin/buildings/${slug}`}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back to Building
          </Link>
        </div>

        {/* Page Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">Analytics</h1>
            <p className="text-sm lg:text-base text-gray-500 mt-1">{building.name}</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Date Range Tabs - Horizontal scroll on mobile */}
            <div className="flex bg-gray-100 rounded-lg p-1 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              {(['7d', '30d', '90d'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                    dateRange === range
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
                </button>
              ))}
            </div>
            <Button variant="secondary" onClick={handleExport} className="w-full sm:w-auto">
              <Download size={16} className="mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Stats Row - 2 cols on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-6 lg:mb-8">
          <StatCard
            icon={QrCode}
            label="QR Scans"
            value={metrics?.qrScans.toString() || '127'}
            change={12}
          />
          <StatCard
            icon={Mail}
            label="Email Clicks"
            value={metrics?.emailClicks.toString() || '24'}
            change={8}
          />
          <StatCard
            icon={Eye}
            label="Unit Views"
            value={metrics?.unitViews.toString() || '342'}
            change={-3}
          />
          <StatCard
            icon={Calendar}
            label="Viewing Requests"
            value={metrics?.viewingRequests.toString() || '18'}
            change={25}
          />
        </div>

        {/* Charts */}
        <TabsRoot defaultValue="overview" className="space-y-4 lg:space-y-6">
          <TabsList className="overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="units">Unit Performance</TabsTrigger>
            <TabsTrigger value="timing">Peak Times</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card className="p-4 lg:p-6">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">
                Traffic Over Time
              </h3>
              <div className="h-56 lg:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      stroke="#9ca3af"
                      fontSize={11}
                      tickMargin={8}
                    />
                    <YAxis stroke="#9ca3af" fontSize={11} width={35} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="views"
                      name="Unit Views"
                      stackId="1"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      fillOpacity={0.2}
                    />
                    <Area
                      type="monotone"
                      dataKey="scans"
                      name="QR Scans"
                      stackId="2"
                      stroke="#10b981"
                      fill="#10b981"
                      fillOpacity={0.2}
                    />
                    <Area
                      type="monotone"
                      dataKey="emails"
                      name="Email Clicks"
                      stackId="3"
                      stroke="#f59e0b"
                      fill="#f59e0b"
                      fillOpacity={0.2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="units">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              <Card className="p-4 lg:p-6">
                <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">
                  Views by Unit Type
                </h3>
                <div className="h-48 lg:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={unitTypeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="views"
                        nameKey="name"
                        label={({ name, percent }) =>
                          `${name ?? ''} ${((percent ?? 0) * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                      >
                        {unitTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-4 lg:p-6">
                <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">
                  Top Viewed Units
                </h3>
                <div className="space-y-3 lg:space-y-4">
                  {[
                    { unit: '1204', type: '1-BR', views: 45 },
                    { unit: '803', type: '2-BR', views: 38 },
                    { unit: '1501', type: '2-BR', views: 32 },
                    { unit: '607', type: '1BR+Den', views: 28 },
                    { unit: '315', type: 'Bachelor', views: 22 },
                  ].map((item, index) => (
                    <div
                      key={item.unit}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900 text-sm lg:text-base">
                            Unit {item.unit}
                          </p>
                          <p className="text-xs lg:text-sm text-gray-500">{item.type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900 text-sm lg:text-base">{item.views}</p>
                        <p className="text-xs lg:text-sm text-gray-500">views</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="timing">
            <Card className="p-4 lg:p-6">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-2 lg:mb-4">
                QR Scans by Hour
              </h3>
              <p className="text-xs lg:text-sm text-gray-500 mb-4">
                Peak activity times help you know when prospects are most likely
                to be interested.
              </p>
              <div className="h-48 lg:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="hour" stroke="#9ca3af" fontSize={11} />
                    <YAxis stroke="#9ca3af" fontSize={11} width={30} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Bar
                      dataKey="scans"
                      name="Scans"
                      fill="#3b82f6"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </TabsContent>
        </TabsRoot>
      </div>
    </AdminLayout>
  );
}

interface StatCardProps {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  change: number;
}

function StatCard({ icon: Icon, label, value, change }: StatCardProps) {
  const isPositive = change >= 0;

  return (
    <Card className="p-4 lg:p-6">
      <div className="flex items-center justify-between mb-3 lg:mb-4">
        <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon size={16} className="text-primary lg:hidden" />
          <Icon size={20} className="text-primary hidden lg:block" />
        </div>
        <span
          className={`text-xs lg:text-sm font-medium ${
            isPositive ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {isPositive ? '+' : ''}
          {change}%
        </span>
      </div>
      <p className="text-xl lg:text-3xl font-bold text-gray-900 mb-0.5 lg:mb-1">{value}</p>
      <p className="text-xs lg:text-sm text-gray-500 truncate">{label}</p>
    </Card>
  );
}
